import { EntityQuery, Parcel, Query, and, is } from '@the-real-insight/tri-model';

import { userAPI } from '../UserAPI';
import { brwFill, brwTooltipLines } from './brwScale';
import { geometryBounds } from './geometryBounds';

/**
 * The Flurstücke of an area, read through the generic query endpoint.
 *
 * No endpoint of its own: `POST /query` already speaks the model's filter language, the parcel
 * collection is not tenant-scoped, and a `Parcel` carries exactly what a map needs. The filter is a
 * range over `centerLat`/`centerLon` — the indexed pair (`center_1`) — against the bounding box of
 * the area, which is how the rest of the portal asks geographic questions too. For a circle that
 * takes in the corners of the square, 4/π of the area, which is a fair price for an indexed answer.
 *
 * A parcel belongs to the box by its centre, not by its outline: a plot on the edge is on the side
 * its middle is on. The same rule the tiles follow, and the one the index can answer.
 *
 * The ceiling below needs care for the same reason. `center_1` is scanned by `centerLat` ascending,
 * so a plain `limit` returns the *southernmost* parcels of the box — measured over a 5 km circle
 * around Halle, the first 1500 covered a 480 m strip along the bottom edge, and the wider the
 * circle the further from the centre that strip sat. What follows therefore asks twice where it has
 * to: once to find out how much latitude 1500 parcels occupy here, and once for a band of that
 * height around the middle. What is shown is then around the point that was chosen, which is what
 * the reader asked about.
 */

/**
 * How many parcels one view may draw.
 *
 * The collection holds 53.8 million, and `POST /query` has no cap of its own — a request without a
 * limit tries to stream them all, geometry included. The ceiling matters at the top of the radius
 * slider, not at the bottom: measured around a city centre, 500 m holds some 1,400 parcels and 1 km
 * some 5,000, but 5 km holds 48,000 in Halle and 87,000 in Cologne, which at ~1.5 kB a document is
 * over a hundred megabytes and a grey mesh on screen. Below the ceiling nothing is hidden; at it,
 * the caller is told.
 */
export const PARCEL_LIMIT = 1500;

/**
 * Above this many parcels the numbers are left off.
 *
 * Each label is a permanent tooltip in the DOM, and at a few hundred plots per screen they overlap
 * into a smear. The number then lives in the hover tooltip, one gesture away.
 */
export const PARCEL_LABEL_LIMIT = 250;

export interface ParcelResult {
    parcels: any[];
    /** True when the ceiling was reached — what is drawn is part of the area, not all of it. */
    truncated: boolean;
}

const parcelsInBox = async (
    minLat: number,
    maxLat: number,
    minLon: number,
    maxLon: number,
): Promise<any[]> => {
    const filter = and(
        is('centerLat').greaterEqual(minLat),
        is('centerLat').lessEqual(maxLat),
        is('centerLon').greaterEqual(minLon),
        is('centerLon').lessEqual(maxLon),
    );

    const rows = await userAPI.query(
        new Query(new EntityQuery(Parcel.type, [], undefined, filter)),
        0,
        // One more than the ceiling: that is how the caller learns the box holds more than it
        // shows, without counting it first — there is no count endpoint to ask.
        PARCEL_LIMIT + 1,
    );

    return Array.isArray(rows) ? rows : [];
};

export const loadParcelsForArea = async (
    geometry: any,
    /** Middle of the area. Without one the box's own middle stands in. */
    centre?: { lat: number; lon: number } | null,
): Promise<ParcelResult> => {
    const bounds = geometryBounds(geometry);
    if (!bounds) {
        return { parcels: [], truncated: false };
    }

    const [[minLon, minLat], [maxLon, maxLat]] = bounds;

    const rows = await parcelsInBox(minLat, maxLat, minLon, maxLon);
    if (rows.length <= PARCEL_LIMIT) {
        return { parcels: rows, truncated: false };
    }

    // Too many. What came back is the bottom edge of the box over its full width — the index is
    // scanned by `centerLat` ascending — and that strip touches the circle at a single point, so
    // nearly all of it lies between the circle and the corner of its box: a band of parcels
    // *below* the area, further south the wider the radius.
    //
    // Its height is the useful measurement, though. It says how much latitude a full load of
    // parcels occupies at this density, and from that a centred window of the same shape as the
    // box follows: with the strip's area (span × width) holding the ceiling, a box of the same
    // aspect holding as many has half-height √(span × height)/2.
    const latitudes = rows.map((parcel: any) => parcel?.centerLat).filter(Number.isFinite);
    const span = latitudes.length > 1 ? Math.max(...latitudes) - Math.min(...latitudes) : 0;

    const height = maxLat - minLat;
    const width = maxLon - minLon;
    const centreLat = centre?.lat ?? (minLat + maxLat) / 2;
    const centreLon = centre?.lon ?? (minLon + maxLon) / 2;

    // A window of no height would come back empty. `span` is only zero where the density is such
    // that a whole load of parcels shares one latitude; a quarter of the box is then the guess.
    const halfLat = span > 0 ? Math.sqrt(span * height) / 2 : height / 4;
    const halfLon = height > 0 ? halfLat * (width / height) : width / 4;

    const banded = await parcelsInBox(
        Math.max(minLat, centreLat - halfLat),
        Math.min(maxLat, centreLat + halfLat),
        Math.max(minLon, centreLon - halfLon),
        Math.min(maxLon, centreLon + halfLon),
    );

    return {
        parcels: banded.slice(0, PARCEL_LIMIT),
        truncated: true,
    };
};

/** What the map draws for one parcel: the outline, its number, and the rest on hover. */
export const parcelElement = (
    parcel: any,
    withLabel: boolean,
    /**
     * Paint the parcels by their Bodenrichtwert instead of as a neutral grid.
     *
     * A mode rather than the new default. The faint fill below is a deliberate choice — the parcels
     * are something to read the map *through*, and at 0.55 opacity they cover the tiles and the
     * satellite image underneath. Both views are wanted; only one at a time.
     */
    brwMode = false,
): any | null => {
    if (!parcel?.geometry) return null;

    const number = typeof parcel.getParcelNumber === 'function' ? parcel.getParcelNumber() : null;
    const brw = parcel.brw ?? null;
    const format = (value: number) => new Intl.NumberFormat('de-DE').format(Math.round(value));

    /**
     * What the plot is — parcel number, where it sits, how big. One line.
     */
    const identity = [
        number ? `Flurstück ${number}` : null,
        parcel.gemarkung ? `Gemarkung ${parcel.gemarkung}` : null,
        parcel.flur ? `Flur ${parcel.flur}` : null,
        parcel.lagebez || null,
        Number.isFinite(parcel.flaecheM2) ? `${Math.round(parcel.flaecheM2)} m²` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    /**
     * The Bodenrichtwert on the second line — all of it, dot-separated, like the first.
     *
     * Two lines, not one and not six. On one line the value sat at the tail behind the Gemarkung,
     * the Flur and the area: the one number the reader came for, in the hardest place to find it.
     * One line per fact was the other extreme — a six-line box for a hover, where the value's own
     * details (Bodenwert, Nutzungsart, Stichtag) are qualifiers of a single statement, not six
     * separate ones. So: what the plot is, then what it is worth.
     *
     * The fill already says "roughly this much"; the tooltip says how much, and whether it is one
     * value or several.
     */
    const wert = brwMode ? brwTooltipLines(brw, format).join(' · ') : '';

    const description: string[] = [identity || null, wert || null].filter(Boolean) as string[];

    const fill = brwMode
        ? brwFill(brw)
        : // Faint on purpose: the parcels are a grid to read the map through, not the subject of
          // it. Whatever is underneath — the tiles, the satellite image — has to stay legible.
          { color: '#C8A24A', opacity: 0.06 };

    return {
        display: {
            type: 'shape',
            shape: parcel.geometry,
            color: '#7A5C1E',
            fillColor: fill.color,
            fillOpacity: fill.opacity,
            weight: 1,
            opacity: 0.85,
            ...(withLabel && number ? { label: number } : {}),
            // One line stays a string, several become an array — the map renders each entry on its
            // own line. Not a Bodenrichtwert special case: a shape with several things to say
            // about it is the general shape of the problem.
            ...(description.length
                ? { tooltip: description.length === 1 ? description[0] : description }
                : {}),
        },
        entity: parcel,
    };
};
